/**
 * The amount pad's arithmetic: digits in, integer cents out — and, for the
 * text box in the middle of it, what a half-typed amount means.
 *
 * The amount is **cents**, whichever way it was entered, and the two ways in
 * are deliberately different things:
 *
 * *The keys are digits, from the right.* A key shifts the number up and lands
 * in the minor unit — 8, 1, 3, 1 is 8, 81, 813, 8131, shown as $81.31 — so
 * there is no decimal point to press and no half-typed string to decide what
 * to do with.
 *
 * *The box is an amount, as written.* Somebody who reaches for their phone's
 * own keypad types `81.31` and means eighty-one thirty-one, so what is in the
 * box goes through `parseMoneyInput` in `src/lib/format.ts` — the same reader
 * every other money box in this app uses, commas, spaces and all — and never
 * through a second implementation of the same thing.
 *
 * Only one of them is state. `cents` is the value; `typed` is the text on its
 * way to becoming a value, and it exists only between the first keystroke in
 * the box and the moment focus leaves it. Every keystroke that is accepted
 * sets both, so the digits on the screen are always the number that will be
 * sent, and a key press throws any half-typed text away rather than trying to
 * merge with it.
 *
 * The ceiling is the API's own (`MAX_AMOUNT_CENTS` in
 * `server/transactions.ts`): anything that would take the amount past it is
 * ignored rather than accepted and refused later, so the pad cannot reach a
 * number the server will not take.
 */

import { centsToDigits, parseMoneyInput } from './format'

/** One billion, in cents — the largest amount the API will accept. */
export const PAD_MAX_CENTS = 100_000_000_000

/**
 * Another digit, from the right. Out-of-range digits and anything that would
 * go past the ceiling leave the amount exactly as it was, so holding a key
 * down stops rather than wrapping.
 */
export function padDigit(cents: number, digit: number): number {
  if (!Number.isInteger(digit) || digit < 0 || digit > 9) return cents
  const next = cents * 10 + digit
  return next > PAD_MAX_CENTS ? cents : next
}

/** Takes the last digit off. `8131` is `813`, and `0` stays `0`. */
export function padBackspace(cents: number): number {
  return Math.trunc(cents / 10)
}

/**
 * What a key press means, or `null` for a key the pad does not handle — so a
 * Tab still moves focus and a Cmd-R still reloads.
 *
 * Escape is deliberately not one of them. A pad that cleared on Escape would
 * silently wipe the amount of somebody who pressed it meaning "close this
 * sheet", and there is no undo on a control whose whole state is one number.
 */
export function padKey(cents: number, key: string): number | null {
  if (/^[0-9]$/.test(key)) return padDigit(cents, Number(key))
  if (key === 'Backspace' || key === 'Delete') return padBackspace(cents)
  return null
}

// ---------------------------------------------------------------------------
// The box
// ---------------------------------------------------------------------------

/** The pad's value, and the text somebody is part-way through typing into it. */
export type PadEntry = {
  /** Integer cents — what will be sent, however it was entered. */
  cents: number
  /** What is in the box, or `null` when the box is only showing `cents`. */
  typed: string | null
}

/**
 * `8131` as the text the box rests at: `81.31`.
 *
 * No symbol and no thousands separators, for the reason `signedMoneyInput`
 * gives: it goes into an `<input>` that has to be re-readable by the thing
 * that reads the box, so leaving and coming back cannot change the amount.
 */
export function padInput(cents: number): string {
  return centsToDigits(cents).replace(/,/g, '')
}

/**
 * Text that is not an amount yet but could still become one: `1`, `12.`,
 * `12.5`, `.5`, or nothing at all.
 *
 * `parseMoneyInput` refuses all but the last of those, and rightly — they are
 * not amounts. But `12.` is what every amount with cents in it looks like
 * half-way through being typed, so the box has to accept the ones that are on
 * the way somewhere and let `parseMoneyInput` say what they are worth.
 */
const PARTIAL = /^\d*(?:\.\d{0,2})?$/

/**
 * Somebody typed `input` into the box.
 *
 * Anything that is not on its way to being an amount — a letter, a second
 * point, a third decimal, a number past the ceiling — is refused by handing
 * back **the very same entry**, so the caller can tell a refusal from a change
 * by identity and put the box back the way it was. That is the same bargain
 * the keys make: a digit the pad cannot take leaves the amount alone rather
 * than being accepted here and rejected by the server later.
 *
 * What is kept is what was typed, exactly — spaces, commas and a trailing
 * point included. Re-writing somebody's text under their caret while they are
 * still typing in it is how a box ends up eating keystrokes; the tidying-up
 * happens when they leave, by dropping `typed` and letting `padInput` say it
 * again.
 */
export function padTyped(entry: PadEntry, input: string): PadEntry {
  const bare = input.replace(/[\s,]/g, '')
  if (!PARTIAL.test(bare)) return entry

  // `.5` is 50 cents and `12.` is 12 dollars; both are said in full before
  // `parseMoneyInput` sees them, because it only reads finished amounts.
  const finished = bare.replace(/^\./, '0.').replace(/\.$/, '')
  const cents = finished === '' ? 0 : parseMoneyInput(finished)
  if (cents === null || cents > PAD_MAX_CENTS) return entry

  return { cents, typed: input }
}
